import type { ApproveRxChangeRequestT } from '@lotushealth/lotushealth_js_sdk/schemas';

import { useApproveRxChangeMutation } from '@/entities/rx';
import { FormField } from '@/shared/ui/primitives';

import { nullIfBlank, nullIfBlankInt } from '../lib';
import { rxChangeApproveDraftSchema, type RxChangeApproveDraft } from '../model/schemas';
import { useDetailAction } from '../model/useDetailAction';
import { DetailActionForm } from './DetailActionForm';

export interface RxChangeApproveFormProps {
  readonly rxChangeId: number;
  /** Called after a successful approve (e.g. to close the parent sheet). */
  readonly onCompleted?: () => void;
}

/**
 * All approve fields are optional in the SDK contract, so empty defaults are
 * safe — DoseSpot accepts the change as-is when nothing is overridden.
 *
 * NOTE: ``priorAuthComment`` stays in the draft schema (commit 7) but is not
 * rendered here — the SDK exposes ``Comment`` (general) and
 * ``PrescriberAuthorizationData`` (auth payload, not a free-text PA note) and
 * neither cleanly carries it. Re-add the field once the backend mapping lands.
 */
const DEFAULT_VALUES: RxChangeApproveDraft = {
  selectedMedicationId: '',
  priorAuthNumber: '',
  priorAuthComment: '',
  comment: '',
};

/**
 * Approve a pending RxChange. Drops ``selectedMedicationId`` when blank — the
 * SDK then defaults to the originally prescribed med. Mutation success
 * invalidates ``rxKeys.pendingRxChangesDetailed()`` (prefix match).
 */
export function RxChangeApproveForm({ rxChangeId, onCompleted }: RxChangeApproveFormProps) {
  const mutation = useApproveRxChangeMutation();
  const { form, handleSubmit } = useDetailAction({
    schema: rxChangeApproveDraftSchema,
    defaultValues: DEFAULT_VALUES,
    mutation,
    buildVariables: (draft: RxChangeApproveDraft) => ({
      rxChangeId,
      data: {
        SelectedMedicationId: nullIfBlankInt(draft.selectedMedicationId),
        PriorAuthNumber: nullIfBlank(draft.priorAuthNumber),
        Comment: nullIfBlank(draft.comment),
      } satisfies ApproveRxChangeRequestT,
    }),
    successMessage: 'RxChange approved.',
    errorTitle: 'Could not approve RxChange.',
    logKey: 'approve RxChange',
    logContext: { rxChangeId },
    onCompleted,
  });

  return (
    <DetailActionForm
      mutation={mutation}
      onSubmit={handleSubmit}
      submitLabel="Approve RxChange"
      pendingLabel="Approving…"
    >
      <FormField
        form={form}
        type="text"
        name="selectedMedicationId"
        label="Selected medication ID"
        description="Optional. Leave blank to accept the prescribed medication."
        placeholder="e.g. 12345"
        inputProps={{ inputMode: 'numeric', autoComplete: 'off' }}
      />
      <FormField
        form={form}
        type="text"
        name="priorAuthNumber"
        label="Prior auth number"
        description="Optional. Required only when the payer demands a PA."
        placeholder="PA reference"
        inputProps={{ autoComplete: 'off' }}
      />
      <FormField
        form={form}
        type="textarea"
        name="comment"
        label="Comment"
        placeholder="Optional note for the pharmacy"
      />
    </DetailActionForm>
  );
}
